import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  SESSION_NOTE_LABEL,
  SESSION_NOTE_STATE,
  SESSION_NOTE_STATE_LABEL,
} from "../group-visit-constant";
import { SessionNoteState } from "../group-visit-types";

export type SessionNoteHeaderProps = {
  groupNoteState: SessionNoteState;
  /** Every present attendee's note is complete. */
  canFinalize: boolean;
  /** Documentation is still open on this session. */
  isPending: boolean;
  /** Every note this session owes, shared and individual, is complete. */
  isDocumentationComplete: boolean;
  /** Closed sessions show the header without actions. */
  isLocked: boolean;
  onCancelSession: () => void;
  onCompleteSession: () => void;
  /** Prints the shared group note with the session's details. */
  onPrint: () => void;
};

/**
 * Title bar of the session documentation screen — the shared note's state and
 * the session-level actions.
 */
const SessionNoteHeader = ({
  groupNoteState,
  canFinalize,
  isPending,
  isDocumentationComplete,
  isLocked,
  onCancelSession,
  onCompleteSession,
  onPrint,
}: SessionNoteHeaderProps) => (
  <Flex
    alignItems="center"
    justifyContent="space-between"
    gap={2}
    flexWrap="wrap"
    sx={{
      flexShrink: 0,
      px: 2,
      py: 1.5,
      backgroundColor: CHART_COLORS.surfaceWhite,
      borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
    }}
  >
    <Flex alignItems="center" gap={1} flexWrap="wrap">
      <Text
        component="h1"
        {...CHART_TYPOGRAPHY.body3}
        fontWeight={CHART_TYPOGRAPHY.weight.medium}
        c={CHART_COLORS.textDarker}
      >
        {SESSION_NOTE_LABEL.title}
      </Text>
      {isDocumentationComplete ? (
        <StatusChip label={SESSION_NOTE_LABEL.completeTag} variant="success" />
      ) : (
        isPending && (
          <StatusChip label={SESSION_NOTE_LABEL.pendingTag} variant="pending" />
        )
      )}
    </Flex>

    <Flex alignItems="center" gap={1.5}>
      <Flex flexDirection="column" alignItems="flex-end">
        <Text
          {...CHART_TYPOGRAPHY.body5}
          c={CHART_COLORS.textMutedGrey}
          textTransform="uppercase"
          letterSpacing="0.04em"
        >
          {SESSION_NOTE_LABEL.groupNoteCaption}
        </Text>
        <Text
          {...CHART_TYPOGRAPHY.body4}
          fontWeight={CHART_TYPOGRAPHY.weight.medium}
          c={
            groupNoteState === SESSION_NOTE_STATE.COMPLETE
              ? CHART_COLORS.badgeSuccessText
              : CHART_COLORS.badgeWarningText
          }
        >
          {SESSION_NOTE_STATE_LABEL[groupNoteState]}
        </Text>
      </Flex>

      <StyledButtonV2
        label={SESSION_NOTE_LABEL.print}
        variant="outlined"
        onClick={onPrint}
        sIcon={<PrintOutlinedIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />}
      />

      {!isLocked && (
        <>
          <StyledButtonV2
            label={SESSION_NOTE_LABEL.cancelSession}
            variant="outlined"
            onClick={onCancelSession}
            sx={{
              color: CHART_COLORS.badgeErrorText,
              borderColor: CHART_COLORS.badgeErrorText,
            }}
          />
          <StyledButtonV2
            label={SESSION_NOTE_LABEL.finalizeSession}
            variant="primary"
            disabled={!canFinalize}
            onClick={onCompleteSession}
          />
        </>
      )}
    </Flex>
  </Flex>
);

export default SessionNoteHeader;
